import { useGetUserFullNameWithFallback } from '@pba/react-utils';
import type { ChatUser } from 'models';
import { useTranslation } from 'react-i18next';
import { Button, DotSeparator, EntitySnippet, Icon } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { UserLink } from 'features/users/components/user-link';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { UserPopover } from 'features/users/components/user-popover';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { useGetNodeUserAvatar } from 'features/users/hooks';
import { useGetListUserDisplayName } from '../../../../../hooks';

type P = {
  canRemove: boolean;
  isAdmin: boolean;
  member: ChatUser;
  onRemove: (member: ChatUser) => void;
};

const ChatGroupInfoMemberItem = ({ canRemove, isAdmin, member, onRemove }: P) => {
  const { t } = useTranslation();
  const getUserFullNameWithFallback = useGetUserFullNameWithFallback();
  const getNodeUserAvatar = useGetNodeUserAvatar();
  const getListUserDisplayName = useGetListUserDisplayName();
  const fullName = getUserFullNameWithFallback(member);
  const displayName = getListUserDisplayName(member.id, fullName);
  const profilePhoto = getNodeUserAvatar({ userId: member.id });

  const descriptionItems = [
    ...(isAdmin ? [{ content: t('Admin'), key: 'admin' }] : []),
    ...(member.email ? [{ content: member.email, key: 'email' }] : []),
  ];

  return (
    <EntitySnippet
      avatarProps={{
        src: profilePhoto,
      }}
      descriptionProps={
        descriptionItems.length > 0 ? { children: <DotSeparator items={descriptionItems} /> } : undefined
      }
      extra={
        canRemove ? (
          <Button
            icon={<Icon color="error" icon="minus-circle-line" />}
            onClick={() => {
              onRemove(member);
            }}
            type="link-secondary"
          />
        ) : undefined
      }
      titleProps={{
        children: displayName,
        ellipsis: true,
        weight: 'medium',
      }}
      wrapAvatar={(avatar) => (
        <UserPopover fallback={{ ...member, profilePhoto }} userId={member.id}>
          <UserLink effects={{ radius: false }} userId={member.id}>
            {avatar}
          </UserLink>
        </UserPopover>
      )}
      wrapTitle={(title) => (
        <UserPopover fallback={{ ...member, profilePhoto }} userId={member.id}>
          <UserLink userId={member.id}>{title}</UserLink>
        </UserPopover>
      )}
    />
  );
};

export default ChatGroupInfoMemberItem;
